<template>
  <v-container class="py-12">
    <TitleHeader>Convênios</TitleHeader>

    <v-row
      class="w-100 max-width mx-auto"
      align="start"
      justify="center"
      no-gutters
    >
      <v-col
        cols="12"
        md="11"
        lg="10"
        xl="9"
      >
        <v-card
          elevation="10"
          class="pa-6 rounded-xl bg-form-card border-left-institucional"
        >
          <template v-if="loading">
            <div
              class="loading-state"
              aria-live="polite"
              role="status"
            >
              <div
                class="spinner"
                aria-hidden="true"
              ></div>
              <p class="loading-text">Carregando informações...</p>
            </div>
          </template>

          <template v-else-if="erro">
            <p class="status-message error">{{ erro }}</p>
          </template>

          <template v-else-if="resultado">
            <div
              class="form-description mb-6"
              v-html="resultado.descricao"
            ></div>

            <v-list
              v-if="resultado.arquivos && resultado.arquivos.length"
              class="mt-6"
              dense
            >
              <v-list-item
                v-for="arquivo in arquivosOrdenados"
                :key="arquivo.id"
                class="arquivo-item"
              >
                <v-list-item-title class="arquivo-titulo">
                  <a
                    :href="urlDownload(arquivo.id)"
                    target="_blank"
                    rel="noopener"
                    class="arquivo-link"
                  >
                    {{ formatarNomeArquivo(arquivo.nome) }}
                  </a>
                </v-list-item-title>
                <v-list-item-subtitle
                  v-if="arquivo.descricao"
                  class="arquivo-descricao"
                >
                  {{ arquivo.descricao }}
                </v-list-item-subtitle>
              </v-list-item>
            </v-list>
          </template>

          <div
            v-else
            class="empty-state"
          >
            <div
              class="empty-icon"
              aria-hidden="true"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
              >
                <path
                  fill="currentColor"
                  d="M6 2a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6H6m7 1.5L18.5 9H13V3.5M8 13h8v2H8v-2m0 4h8v2H8v-2m0-8h5v2H8V9Z"
                />
              </svg>
            </div>
            <h2 class="empty-title">Nenhum resultado encontrado</h2>
            <p class="empty-description">Ainda não há registros disponíveis nesta seção<br /></p>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useRuntimeConfig } from '#imports'
import TitleHeader from '@/components/TitleHeader.vue'
import { formatarNomeArquivo } from '@/utils/arquivo'

definePageMeta({
  middleware: 'manutencao',
  title: 'FUNARPEN - Convênios',
})

useHead({
  title: 'Convênios',
  meta: [{ name: 'description', content: 'Convênios do FUNARPEN' }],
})

const config = useRuntimeConfig()
const baseUrl = config.public.backofficeApiBaseUrl
const router = useRouter()
const resultado = ref(null)
const loading = ref(true)
const erro = ref('')

const arquivosOrdenados = computed(() => [...(resultado.value?.arquivos ?? [])].sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0)))

function urlDownload(id) {
  return `${baseUrl}/portal/arquivos/baixar/${id}`
}

async function buscarPagina() {
  try {
    const res = await fetch(`${baseUrl}/portal/paginas/buscar-pagina?slug=funarpen-convenios`)

    if (res.status === 401) {
      await router.push('/manutencao/acesso-negado')
      return
    }

    if (res.status === 404) return
    if (!res.ok) throw new Error('Erro ao buscar dados')

    const data = await res.json()
    resultado.value = data && Object.keys(data).length ? data : null
  } catch (error) {
    erro.value = error.message.includes('Failed to fetch') ? 'Erro de conexão. Tente novamente mais tarde.' : error.message
  } finally {
    loading.value = false
  }
}

onMounted(buscarPagina)
</script>

<style scoped>
.max-width {
  max-width: 1200px;
  box-sizing: border-box;
}

.mx-auto {
  margin-left: auto;
  margin-right: auto;
}

.bg-form-card {
  background-color: rgba(255, 255, 255, 0.92);
}

.border-left-institucional {
  border-left: 6px solid var(--funarpen-blue);
}

.arquivo-item {
  padding: 0.8rem 0;
  border-bottom: 1px dashed rgba(8, 47, 73, 0.14);
}

.arquivo-titulo {
  white-space: normal;
  word-break: break-word;
}

.arquivo-link {
  color: var(--funarpen-blue);
  font-weight: 600;
  text-decoration: none;
}

.arquivo-link:hover {
  text-decoration: underline;
}

.arquivo-descricao {
  font-size: 0.95rem;
  margin-top: 0.3rem;
  color: var(--funarpen-muted);
  white-space: normal;
  word-break: break-word;
}

.status-message {
  font-size: 1.2rem;
  text-align: center;
  color: var(--funarpen-red);
  font-weight: 600;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  text-align: center;
  color: rgba(11, 69, 113, 0.8);
}

.spinner {
  width: 64px;
  height: 64px;
  border: 6px solid rgba(11, 69, 113, 0.2);
  border-top-color: rgba(11, 69, 113, 0.7);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1.5rem;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-text {
  font-size: 1.25rem;
  font-weight: 500;
}

.empty-state {
  text-align: center;
  color: rgba(11, 69, 113, 0.85);
}

.empty-icon svg {
  width: 72px;
  height: 72px;
  fill: rgba(11, 69, 113, 0.6);
}

.empty-title {
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
}

.empty-description {
  font-size: 1.125rem;
  font-weight: 400;
  color: rgba(11, 69, 113, 0.75);
  line-height: 1.6;
}

.form-description {
  padding: 1rem 1.25rem;
  background-color: rgba(11, 79, 108, 0.08);
  border-left: 4px solid var(--funarpen-gold);
  border-radius: 6px;
  margin-bottom: 1.5rem;
}

.form-description p {
  margin: 0;
  color: var(--funarpen-ink);
  font-size: 0.95rem;
  line-height: 1.5;
}
</style>
